import {
  AdministrationApproach,
  ClinicalEventType,
  ClinicalProductType,
  PrescribableItemType,
  RateUnit,
  TimeUnit,
} from "elephant-web/gql";
import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_PRESCRIPTION_COLLECTION_NAME = "prescription";

// Type-safe wrappers for all rxdb-hooks hooks.
export const useRxCollectionPrescription =
  (): RxCollection<Prescription> | null =>
    useRxCollection<Prescription>(RXDB_PRESCRIPTION_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<Prescription>>;
export type PrescriptionQueryConstructor = UseRxData["1"];

// TODO: Get this into GQL
// Lifted from src/client/inpatient/prescription/hooks/useDoseUnits.ts
export enum DoseUnit {
  Mg = "mg",
  Micrograms = "micrograms",
  G = "g",
  Tablet = "tablet",
  Ml = "ml",
  Drop = "drop",
  Puff = "puff",
  Unit = "unit",
  Capsule = "capsule",
  Application = "application",
  CustomDose = "customDose",
  Patch = "patch",
  Sachet = "sachet",
  Suppository = "suppository",
  Pessary = "pessary",
  Device = "device",
  Spray = "spray",
  Dose = "dose",
}

export const useRxDataPrescription = (
  queryConstructor: UseRxData["1"],
  options?: UseRxData["2"],
) =>
  useRxData<Prescription>(
    RXDB_PRESCRIPTION_COLLECTION_NAME,
    queryConstructor,
    options,
  );

const PRESCRIPTION_SCHEMA_LITERAL = {
  title: "prescription schema",
  version: 9,
  description: "Represents a prescription for a patient",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    administrationApproach: {
      type: "string",
      enum: Object.values(AdministrationApproach),
    },
    cancellationDetails: {
      type: ["object", "null"],
      properties: {
        at: {
          type: "string",
          format: "date-time",
        },
        by: {
          type: ["string", "null"],
        },
        reason: {
          type: "string",
        },
      },
      required: ["at", "reason", "by"],
    },
    clinicalEvent: {
      type: ["object"],
      properties: {
        id: {
          type: "string",
          maxLength: 100,
        },
        type: {
          type: "string",
          enum: Object.values(ClinicalEventType),
        },
      },
      required: ["id", "type"],
    },
    prescribableItemType: {
      type: "string",
      enum: Object.values(PrescribableItemType),
    },
    prescribableItemFreeText: {
      type: ["string", "null"],
    },
    clinicalProduct: {
      type: ["object", "null"],
      properties: {
        id: {
          type: "string",
          maxLength: 100,
        },
        type: {
          type: "string",
          enum: Object.values(ClinicalProductType),
        },
      },
      required: ["id", "type"],
    },
    dosage: {
      description: "Take two tablets",
      type: "object",
      properties: {
        value: {
          type: "number",
        },
        unit: {
          type: "string",
          enum: Object.values(DoseUnit),
        },
      },
      required: ["value", "unit"],
    },
    facilityId: {
      type: "string",
      maxLength: 100,
    },
    frequency: {
      description: "(Poorly named) For three days",
      type: ["object", "null"],
      properties: {
        value: {
          type: "number",
        },
        unit: {
          type: "string",
          enum: Object.values(TimeUnit),
        },
      },
      required: ["value", "unit"],
    },
    meta: SYNCABLE_META,
    minimumDoseInterval: {
      type: ["object", "null"],
      properties: {
        value: {
          type: "number",
        },
        unit: {
          type: "string",
          enum: Object.values(TimeUnit),
        },
      },
      required: ["value", "unit"],
    },
    note: {
      type: ["string", "null"],
    },
    patientId: {
      type: "string",
      maxLength: 100,
    },
    prescribedByUserId: {
      type: "string",
      maxLength: 100,
    },
    rate: {
      description: "Twice a day",
      type: ["object", "null"],
      properties: {
        value: {
          type: "number",
        },
        unit: {
          type: "string",
          enum: Object.values(RateUnit),
        },
      },
      required: ["value", "unit"],
    },
    startsAt: {
      type: "string",
      format: "date-time",
    },
    stopsAt: {
      type: "string",
      format: "date-time",
    },
  },
  required: [
    "id",
    "administrationApproach",
    "clinicalEvent",
    "prescribableItemType",
    "prescribableItemFreeText",
    "clinicalProduct",
    "dosage",
    "rate",
    "facilityId",
    "frequency",
    "meta",
    "patientId",
    "prescribedByUserId",
    "startsAt",
    "stopsAt",
  ],
  // https://elephant.slab.com/posts/offline-schema-fields-uwb0pbii#huay7-rxdb-dexie-elephant-6-prescription
  encrypted: [
    "administrationApproach",
    "clinicalEvent",
    "prescribableItemType",
    "prescribableItemFreeText",
    "clinicalProduct",
    "dosage",
    "rate",
    "frequency",
    "meta",
    "startsAt",
    "stopsAt",
  ],
  indexes: ["patientId", "facilityId", "prescribedByUserId"],

  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(PRESCRIPTION_SCHEMA_LITERAL);

// aggregate the document type from the schema
export type Prescription = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

// create the typed RxJsonSchema from the literal typed object.
export const PRESCRIPTION_SCHEMA: RxJsonSchema<Prescription> =
  PRESCRIPTION_SCHEMA_LITERAL;
